// Clinical Performance Index (CPI) — 10 interconnected physiological domains.
// Sample scores below power the fictional/de-identified demo profile used on
// the homepage and /sample-analysis. They are illustrative only.

export const CPI_DOMAINS = [
  { code: "MET", name: "Metabolic", score: 48 },
  { code: "HRM", name: "Hormonal / Neuroendocrine", score: 61 },
  { code: "ENR", name: "Energy / Mitochondrial", score: 44 },
  { code: "SLP", name: "Sleep / Recovery", score: 52 },
  { code: "STR", name: "Stress / Autonomic", score: 46 },
  { code: "GUT", name: "Gut / Microbiome", score: 67 },
  { code: "CVD", name: "Cardiovascular", score: 72 },
  { code: "INF", name: "Inflammation / Immune", score: 58 },
  { code: "NEU", name: "Neurocognitive", score: 70 },
  { code: "NTR", name: "Nutrient / Methylation", score: 55 },
];

const profileDomains = (scores) =>
  CPI_DOMAINS.map((domain, index) => ({ ...domain, score: scores[index] }));

export const CPI_PROFILES = [
  {
    id: "under-recovered",
    label: "Recovery",
    name: "Under-Recovered Metabolic",
    cpi: 57,
    summary: "Recovery strain is compressing metabolic efficiency and daytime energy.",
    priorities: ["Sleep architecture", "Insulin sensitivity", "Stress load"],
    domains: profileDomains([48, 61, 44, 52, 46, 67, 72, 58, 70, 55]),
  },
  {
    id: "hormonal-drift",
    label: "Hormonal",
    name: "Hormonal Recovery Drift",
    cpi: 63,
    summary: "Hormonal and nutrient signals are limiting otherwise stable performance systems.",
    priorities: ["Hormonal context", "Micronutrient status", "Strength recovery"],
    domains: profileDomains([66, 43, 58, 61, 55, 71, 77, 64, 72, 49]),
  },
  {
    id: "resilient-performer",
    label: "Performance",
    name: "Resilient Performer",
    cpi: 76,
    summary: "Strong cardiovascular capacity with targeted opportunities in recovery and gut resilience.",
    priorities: ["Recovery consistency", "Gut diversity", "Longitudinal tracking"],
    domains: profileDomains([78, 74, 81, 66, 69, 64, 88, 73, 82, 75]),
  },
];

export const TIERS = [
  {
    n: 1,
    name: "Foundations",
    body: "Sleep, nutrition, movement and recovery.",
  },
  {
    n: 2,
    name: "Corrective Optimization",
    body: "Targeted nutrient and metabolic correction.",
  },
  {
    n: 3,
    name: "Clinician-Managed Optimization",
    body: "Clinician-managed medications and hormones.",
  },
  {
    n: 4,
    name: "Advanced Optimization",
    body: "Advanced diagnostics and precision options.",
  },
];

export const MILESTONES = [
  { label: "Baseline", cpi: 54, bodyFat: "28.4%", sleep: "5h 48m", energy: "4 / 10" },
  { label: "30 Days", cpi: 58, bodyFat: "27.1%", sleep: "6h 24m", energy: "6 / 10" },
  { label: "90 Days", cpi: 66, bodyFat: "24.6%", sleep: "6h 55m", energy: "7 / 10" },
  { label: "6 Months", cpi: 74, bodyFat: "22.1%", sleep: "7h 12m", energy: "8 / 10" },
];

export const SAMPLE_PROFILE = {
  id: "SAMPLE-0427",
  meta: "Male · 42 · Goals: energy, body composition, long-term health",
  archetype: {
    name: "The Under-Recovered Metabolic Pattern",
    body: "Energy, stress-recovery and metabolic domains are compressing each other: shortened sleep raises autonomic load, which blunts insulin sensitivity and daytime energy. The highest-leverage work is restoring recovery capacity while correcting early metabolic drift.",
  },
  topSystems: ["ENR", "STR", "MET"],
  symptoms: [
    "Afternoon energy crashes",
    "Poor sleep quality and slow morning start",
    "Stubborn abdominal fat despite training",
    "Brain fog during long work blocks",
    "Slower recovery between workouts",
  ],
  relationships: [
    { symptom: "Afternoon energy crashes", systems: ["Metabolic", "Sleep / Recovery", "Energy / Mitochondrial"] },
    { symptom: "Stubborn abdominal fat", systems: ["Metabolic", "Hormonal / Neuroendocrine", "Stress / Autonomic"] },
    { symptom: "Brain fog during work blocks", systems: ["Neurocognitive", "Sleep / Recovery", "Inflammation / Immune"] },
    { symptom: "Slower workout recovery", systems: ["Stress / Autonomic", "Energy / Mitochondrial", "Nutrient / Methylation"] },
  ],
  bodyComp: [
    { label: "Body fat", value: "28.4%", note: "above optimal range" },
    { label: "Skeletal muscle mass", value: "36.1 kg", note: "slightly below target" },
    { label: "Visceral adipose", value: "Level 12", note: "elevated" },
    { label: "Phase angle", value: "5.6°", note: "low-normal cellular health" },
  ],
  energyRecovery: [
    { label: "Avg HRV (30 days)", value: "38 ms", note: "trending low" },
    { label: "Sleep duration", value: "5h 48m", note: "below 7h target" },
    { label: "Deep sleep", value: "48 min", note: "low" },
    { label: "Resting heart rate", value: "66 bpm", note: "room to improve" },
  ],
  labs: [
    { label: "Fasting insulin", value: "14.2 µIU/mL", note: "elevated" },
    { label: "HbA1c", value: "5.6%", note: "upper-normal" },
    { label: "hs-CRP", value: "2.1 mg/L", note: "borderline inflammation" },
    { label: "Vitamin D", value: "24 ng/mL", note: "insufficient" },
    { label: "ApoB", value: "105 mg/dL", note: "above optimal" },
    { label: "Free testosterone", value: "Low-normal", note: "monitor" },
  ],
  priorities: [
    "Rebuild sleep architecture and circadian consistency — Sleep / Recovery, Stress / Autonomic",
    "Restore insulin sensitivity through nutrition timing and zone-2 volume — Metabolic",
    "Correct vitamin D status and methylation support — Nutrient / Methylation",
    "Progressive strength block to protect muscle during fat loss — Body composition",
    "Re-test key labs and re-score the CPI at 90 days — Longitudinal",
  ],
};
